/**
 * Custom Toast Notification System
 * Built from scratch without any external packages
 */

// Toast container management
let toastContainer = null;

/**
 * Dispatch event to open notification dropdown with highlight
 * @param {string|number} notificationId - The ID of the notification to highlight
 */
export const openNotificationDropdown = (notificationId) => {
  const event = new CustomEvent('open-notification-dropdown', {
    detail: { notificationId }
  });
  window.dispatchEvent(event);
};

/**
 * Dispatch event to open post detail modal
 * @param {string|number} postId - The ID of the post to display
 */
export const openPostModal = (postId) => {
  const event = new CustomEvent('open-post-modal', {
    detail: { postId }
  });
  window.dispatchEvent(event);
};

/**
 * Extract notification ID from various notification structures
 * @param {object} notification - The notification object
 * @returns {string|number|null} - The notification ID or null
 */
const extractNotificationId = (notification) => {
  if (!notification) return null;

  // Try various possible ID fields in order of likelihood
  if (notification.id) return notification.id;
  if (notification.notification_id) return notification.notification_id;
  if (notification.notificationId) return notification.notificationId;

  // Check nested data structures
  if (notification.data) {
    if (typeof notification.data === 'object' && !Array.isArray(notification.data)) {
      if (notification.data.id) return notification.data.id;
      if (notification.data.notification_id) return notification.data.notification_id;
    } else if (typeof notification.data === 'string') {
      // Try to parse JSON string
      try {
        const parsed = JSON.parse(notification.data);
        if (parsed.id) return parsed.id;
        if (parsed.notification_id) return parsed.notification_id;
      } catch (e) {
        // Not JSON, ignore
      }
    }
  }

  return null;
};

// Initialize toast container
const initializeToastContainer = () => {
  if (toastContainer) return toastContainer;

  // Create container
  toastContainer = document.createElement('div');
  toastContainer.id = 'custom-toast-container';
  toastContainer.style.cssText = `
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 12px;
    pointer-events: none;
  `;

  document.body.appendChild(toastContainer);
  return toastContainer;
};

// Helper function to get relative time (e.g., "2m ago", "1h ago")
const getRelativeTime = () => {
  // Since notifications are shown immediately, return "Just now" or "now"
  // For future enhancement, this could accept a timestamp parameter
  return 'now';
};

// Create toast element
const createToastElement = (message, type = 'info', options = {}) => {
  const {
    duration = 5000,
    title = '',
    image = '',
    buttons = [],
    customStyle = {},
    mediaPreview = null,
    clickable = true
  } = options;

  const toast = document.createElement('div');
  toast.className = `custom-toast custom-toast-${type}`;

  // Base styles - Facebook-like modern design
  const baseStyles = {
    background: '#ffffff',
    color: '#050505',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    border: 'none',
    padding: '12px',
    marginBottom: '0',
    minWidth: '300px',
    maxWidth: '360px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: '15px',
    lineHeight: '1.3333',
    opacity: '0',
    transform: 'translateX(100%) scale(0.95)',
    transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: 'auto',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    cursor: 'default',
    ...customStyle
  };

  // Add type-specific styling (left border accent)
  const typeStyles = {
    error: {
      borderLeft: '4px solid #e41e3f',
      boxShadow: '0 4px 12px rgba(228, 30, 63, 0.2)'
    },
    success: {
      borderLeft: '4px solid #42b72a',
      boxShadow: '0 4px 12px rgba(66, 183, 42, 0.15)'
    },
    warning: {
      borderLeft: '4px solid #f39c12',
      boxShadow: '0 4px 12px rgba(243, 156, 18, 0.15)'
    },
    info: {
      borderLeft: '4px solid #1877f2',
      boxShadow: '0 4px 12px rgba(24, 119, 242, 0.15)'
    }
  };

  // Add hover effect with type-specific shadow
  const hoverShadows = {
    error: '0 6px 16px rgba(228, 30, 63, 0.3)',
    success: '0 6px 16px rgba(66, 183, 42, 0.25)',
    warning: '0 6px 16px rgba(243, 156, 18, 0.25)',
    info: '0 6px 16px rgba(24, 119, 242, 0.25)'
  };
  const defaultHoverShadow = hoverShadows[type] || hoverShadows.info;
  const defaultShadow = typeStyles[type]?.boxShadow || typeStyles.info.boxShadow;

  toast.onmouseenter = () => {
    toast.style.transform = 'translateX(0) scale(1.02)';
    toast.style.boxShadow = defaultHoverShadow;
  };
  toast.onmouseleave = () => {
    toast.style.transform = 'translateX(0) scale(1)';
    toast.style.boxShadow = defaultShadow;
  };

  // Apply base styles with type-specific accent
  const finalStyles = {
    ...baseStyles,
    ...(typeStyles[type] || typeStyles.info)
  };
  Object.assign(toast.style, finalStyles);

  // Create content structure - Facebook-like layout
  const content = document.createElement('div');
  content.style.cssText = `
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-height: 40px;
    overflow: hidden;
  `;

  // Add avatar if provided - larger, modern styling
  if (image) {
    const avatarContainer = document.createElement('div');
    avatarContainer.style.cssText = `
      width: 40px;
      height: 40px;
      border-radius: 50%;
      overflow: hidden;
      flex-shrink: 0;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: transform 0.2s ease;
      border: 2px solid rgba(255, 255, 255, 0.8);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    `;

    // Add hover effect to avatar
    avatarContainer.onmouseenter = () => {
      avatarContainer.style.transform = 'scale(1.05)';
    };
    avatarContainer.onmouseleave = () => {
      avatarContainer.style.transform = 'scale(1)';
    };

    const img = document.createElement('img');
    img.src = image;
    img.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    `;
    img.onerror = () => {
      const initials = getInitials(title || message);
      avatarContainer.innerHTML = `<span style="font-size: 16px; font-weight: 600; color: white; letter-spacing: 0.5px;">${initials}</span>`;
    };
    avatarContainer.appendChild(img);
    toast.appendChild(avatarContainer);
  }

  // Add title if provided - bold sender name style
  if (title) {
    const titleElement = document.createElement('div');
    titleElement.textContent = title;
    titleElement.style.cssText = `
      font-weight: 600;
      font-size: 15px;
      margin: 0;
      color: #050505;
      line-height: 1.3333;
      word-wrap: break-word;
    `;
    content.appendChild(titleElement);
  }

  // Add message - hide if media preview is present
  if (!mediaPreview || !mediaPreview.url) {
    const messageElement = document.createElement('div');
    // Support multi-line messages (replace \n with <br>)
    messageElement.innerHTML = message.replace(/\n/g, '<br>');
    messageElement.style.cssText = `
      font-size: 15px;
      margin: 0;
      line-height: 1.3333;
      color: #65676b;
      white-space: pre-line;
      word-wrap: break-word;
      margin-top: ${title ? '2px' : '0'};
    `;
    content.appendChild(messageElement);
  }

  // Add timestamp - relative time display
  const timestampElement = document.createElement('div');
  timestampElement.textContent = getRelativeTime();
  timestampElement.style.cssText = `
    font-size: 13px;
    color: #8a8d91;
    margin: 0;
    margin-top: 4px;
    font-weight: 400;
  `;
  content.appendChild(timestampElement);

  // Add media preview if provided - modern styling
  if (mediaPreview && mediaPreview.url) {
    const mediaContainer = document.createElement('div');
    mediaContainer.style.cssText = `
      margin-top: 8px;
      border-radius: 8px;
      overflow: hidden;
      width: 100%;
      max-width: 240px;
      height: 120px;
      background: #f0f2f5;
      border: 1px solid #e4e6eb;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      cursor: pointer;
      transition: transform 0.2s ease;
    `;

    // Add hover effect to media preview
    mediaContainer.onmouseenter = () => {
      mediaContainer.style.transform = 'scale(1.02)';
    };
    mediaContainer.onmouseleave = () => {
      mediaContainer.style.transform = 'scale(1)';
    };

    if (mediaPreview.isImage) {
      const img = document.createElement('img');
      img.src = mediaPreview.url;
      img.alt = mediaPreview.name || 'Media preview';
      img.style.cssText = `
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      `;
      img.onerror = () => {
        // Fallback for failed image load
        mediaContainer.innerHTML = `
          <div style="
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
            background: #f0f2f5;
            color: #8a8d91;
            font-size: 24px;
          ">
            📷
          </div>
        `;
      };
      mediaContainer.appendChild(img);
    } else if (mediaPreview.isVideo) {
      mediaContainer.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: #f0f2f5;
          color: #8a8d91;
          font-size: 32px;
          gap: 8px;
        ">
          <span>🎥</span>
          <span style="font-size: 13px; color: #65676b;">Video</span>
        </div>
      `;
    } else {
      // Document or other file type
      mediaContainer.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: #f0f2f5;
          color: #8a8d91;
          font-size: 32px;
          gap: 8px;
        ">
          <span>📄</span>
          <span style="font-size: 13px; color: #65676b;">${mediaPreview.name || 'File'}</span>
        </div>
      `;
    }

    content.appendChild(mediaContainer);
  }

  // Add buttons if provided - modern Facebook-style buttons
  if (buttons && buttons.length > 0) {
    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
      display: flex;
      gap: 8px;
      margin-top: 12px;
      flex-wrap: wrap;
    `;

    buttons.slice(0, 3).forEach(button => {
      const btn = document.createElement('button');
      btn.textContent = button.text;
      btn.onclick = (e) => {
        e.stopPropagation();
        if (button.onClick) button.onClick();
      };

      // Determine button style
      let buttonStyle = '';
      if (button.primary) {
        buttonStyle = `
          background: #1877f2;
          color: white;
        `;
      } else if (button.success) {
        buttonStyle = `
          background: #42b72a;
          color: white;
        `;
      } else if (button.danger) {
        buttonStyle = `
          background: #e41e3f;
          color: white;
        `;
      } else if (button.warning) {
        buttonStyle = `
          background: #f39c12;
          color: white;
        `;
      } else {
        buttonStyle = `
          background: #e4e6eb;
          color: #050505;
        `;
      }

      btn.style.cssText = `
        padding: 8px 16px;
        border: none;
        border-radius: 6px;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        ${buttonStyle}
        flex: 1;
        min-width: 80px;
      `;

      // Store original background color for hover effects
      let originalBg = '';
      if (button.primary) {
        originalBg = '#1877f2';
      } else if (button.success) {
        originalBg = '#42b72a';
      } else if (button.danger) {
        originalBg = '#e41e3f';
      } else if (button.warning) {
        originalBg = '#f39c12';
      } else {
        originalBg = '#e4e6eb';
      }

      // Add hover effects
      btn.onmouseenter = () => {
        if (button.primary) {
          btn.style.background = '#166fe5';
        } else if (button.success) {
          btn.style.background = '#36a420';
        } else if (button.danger) {
          btn.style.background = '#d0193a';
        } else if (button.warning) {
          btn.style.background = '#e67e22';
        } else {
          btn.style.background = '#d8dadf';
        }
        btn.style.transform = 'scale(1.02)';
      };
      btn.onmouseleave = () => {
        btn.style.background = originalBg;
        btn.style.transform = 'scale(1)';
      };

      buttonContainer.appendChild(btn);
    });

    content.appendChild(buttonContainer);
  }

  toast.appendChild(content);

  // Add close button - modern styling
  const closeButton = document.createElement('button');
  closeButton.innerHTML = '×';
  closeButton.style.cssText = `
    background: #e4e6eb;
    border: none;
    font-size: 20px;
    font-weight: 400;
    color: #65676b;
    cursor: pointer;
    padding: 0;
    margin: 0;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
    opacity: 0.8;
  `;
  closeButton.onclick = (e) => {
    e.stopPropagation();
    removeToast(toast);
  };
  closeButton.onmouseenter = () => {
    closeButton.style.background = '#d8dadf';
    closeButton.style.opacity = '1';
    closeButton.style.transform = 'scale(1.1)';
  };
  closeButton.onmouseleave = () => {
    closeButton.style.background = '#e4e6eb';
    closeButton.style.opacity = '0.8';
    closeButton.style.transform = 'scale(1)';
  };
  toast.appendChild(closeButton);

  return toast;
};

// Helper function to get initials
const getInitials = (name) => {
  if (!name || typeof name !== 'string') return 'U';
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Remove toast with animation - smooth fade out
const removeToast = (toast) => {
  toast.style.opacity = '0';
  toast.style.transform = 'translateX(100%) scale(0.95)';

  setTimeout(() => {
    if (toast.parentNode) {
      toast.parentNode.removeChild(toast);
    }
  }, 350);
};

// Show toast with animation - smooth slide in
const showToast = (toast) => {
  const container = initializeToastContainer();
  container.appendChild(toast);

  // Trigger animation with slight delay for smooth entrance
  setTimeout(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateX(0) scale(1)';
  }, 10);
};

/**
 * Main notification function with optional buttons
 */
export const showNotificationToast = (notification, options = {}) => {
  const {
    type = options.type || 'info',
    title = notification?.title || '',
    message = notification?.message || notification?.body || '',
    duration = 5000,
    position = 'bottom-right',
    image = options.image || notification?.image || notification?.sender_image || notification?.sender?.image || '',
    buttons = [],
    customStyle = {},
    mediaPreview = null,
    onClick = null, // Add onClick support
    clickable = options.clickable !== undefined ? options.clickable : true
  } = options;


  // Extract notification ID for dropdown highlighting
  const notificationId = extractNotificationId(notification);

  // Create toast element
  const toast = createToastElement(message, type, {
    duration,
    title,
    image,
    buttons,
    customStyle,
    mediaPreview
  });

  // Check if this is a reaction or comment notification that should open post modal
  const notificationType = notification?.type || notification?.data?.type || options?.type;
  const isReactionOrComment = notificationType === 'reaction' ||
    notificationType === 'post_reaction' ||
    notificationType === 'story_reaction' ||
    notificationType === 'new_reaction' ||
    notificationType === 'like' ||
    notificationType === 'comment' ||
    notificationType === 'comment_reply' ||
    notificationType === 'new_comment' ||
    notificationType === 'video_processing_completed'; // Added video notification

  // Extract post ID for reaction/comment notifications
  let postId = null;
  if (isReactionOrComment) {
    // Try to parse data if it's a JSON string
    let parsedData = notification?.data || notification?.parsedData || {};
    if (typeof parsedData === 'string') {
      try {
        parsedData = JSON.parse(parsedData);
      } catch (e) {
        // If parsing fails, use empty object
        parsedData = {};
      }
    }

    // Also check nested data structure
    if (!parsedData || Object.keys(parsedData).length === 0) {
      parsedData = notification?.data?.data || notification?.parsedData?.data || {};
    }

    const contentType = parsedData.content_type || parsedData.data?.content_type || notification?.data?.content_type;

    if (notificationType === 'video_processing_completed') {
      // For video notifications, directly get post_id
      // Check all possible locations
      postId = notification?.post_id ||
        notification?.data?.post_id ||
        parsedData?.post_id ||
        parsedData?.data?.post_id ||
        notification?.target_id;
      console.log('🎥 [Video Notification] Extracted post ID:', postId, 'from notification:', notification, 'notificationType:', notificationType);
    } else if (notificationType === 'comment' || notificationType === 'comment_reply' || notificationType === 'new_comment') {
      // For comment notifications, get post_id
      postId = parsedData.post_id ||
        parsedData.data?.post_id ||
        notification?.data?.post_id ||
        notification?.post_id ||
        notification?.target_id ||
        parsedData.postId;
    } else if (notificationType === 'reaction' || notificationType === 'post_reaction' || notificationType === 'story_reaction' || notificationType === 'new_reaction' || notificationType === 'like') {
      // For reaction notifications, check content_type
      if (contentType === "App\\Models\\Post" || contentType === "App\\Models\\Post" || contentType === "Post") {
        // Direct post reaction - use content_id as post_id
        postId = parsedData.content_id ||
          parsedData.data?.content_id ||
          notification?.data?.content_id ||
          parsedData.contentId;
      } else if (contentType === "App\\Models\\PostComment" || contentType === "App\\Models\\PostComment" || contentType === "PostComment") {
        // Comment reaction - try to get post_id from parsedData
        postId = parsedData.post_id ||
          parsedData.data?.post_id ||
          notification?.data?.post_id ||
          notification?.post_id ||
          parsedData.postId;
      } else {
        // Fallback: try all possible locations
        postId = parsedData.post_id ||
          parsedData.data?.post_id ||
          notification?.data?.post_id ||
          notification?.post_id ||
          notification?.target_id ||
          notification?.content_id ||
          parsedData.content_id ||
          parsedData.data?.content_id ||
          notification?.data?.content_id ||
          parsedData.postId ||
          parsedData.contentId;
      }
    }

    // Log for debugging
    if (isReactionOrComment) {
    }
  }

  // Make toast clickable by default (if clickable is true)
  // For reaction/comment notifications, open post modal
  // Otherwise, open notification dropdown with highlight
  if (clickable) {
    if (!onClick && postId && isReactionOrComment) {
      toast.style.cursor = 'pointer';
      toast.addEventListener('click', (e) => {
        // Don't trigger if clicking on close button or action buttons
        if (e.target.closest('button')) {
          return;
        }
        // Open post modal
        openPostModal(postId);
      });
    } else if (!onClick && notificationId) {
      toast.style.cursor = 'pointer';
      toast.addEventListener('click', (e) => {
        // Don't trigger if clicking on close button or action buttons
        if (e.target.closest('button')) {
          return;
        }
        // Open notification dropdown with highlight
        openNotificationDropdown(notificationId);
      });
    } else if (onClick && typeof onClick === 'function') {
      // Custom onClick handler provided
      toast.style.cursor = 'pointer';
      toast.addEventListener('click', (e) => {
        // Don't trigger onClick if clicking on close button or buttons
        if (e.target.closest('button')) {
          return;
        }
        onClick(e);
      });
    } else if (notificationId) {
      // No custom onClick but has notification ID - make clickable
      toast.style.cursor = 'pointer';
      toast.addEventListener('click', (e) => {
        // Don't trigger if clicking on close button or action buttons
        if (e.target.closest('button')) {
          return;
        }
        // Open notification dropdown with highlight
        openNotificationDropdown(notificationId);
      });
    }
  } else {
    // Explicitly set cursor to default if not clickable
    toast.style.cursor = 'default';
  }

  // Show toast
  showToast(toast);

  // Auto-remove after duration (unless buttons are present and duration is 0)
  if (duration > 0 || (buttons.length === 0 && duration === 0)) {
    setTimeout(() => removeToast(toast), duration || 5000);
  }

  // Return toast object for manual control
  return {
    id: Date.now(),
    element: toast,
    dismiss: () => removeToast(toast),
    update: (newOptions) => {
      // Update toast content
      const newToast = createToastElement(
        newOptions.message || message,
        newOptions.type || type,
        { ...options, ...newOptions }
      );
      if (toast.parentNode) {
        toast.parentNode.replaceChild(newToast, toast);
      }
      return newToast;
    }
  };
};

/**
 * Convenience functions for common notification types
 */
export const showSuccessToast = (message, options = {}) => {
  return showNotificationToast({ body: message }, { type: 'success', ...options });
};

export const showErrorToast = (message, options = {}) => {
  return showNotificationToast({ body: message }, { type: 'error', ...options });
};

export const showWarningToast = (message, options = {}) => {
  return showNotificationToast({ body: message }, { type: 'warning', ...options });
};

export const showInfoToast = (message, options = {}) => {
  return showNotificationToast({ body: message }, { type: 'info', ...options });
};

/**
 * Convenience functions for notifications with buttons
 */
export const showConfirmationToast = (message, onConfirm, onCancel, options = {}) => {
  return showNotificationToast({ body: message }, {
    type: 'info',
    duration: 0, // Don't auto-dismiss
    buttons: [
      {
        text: 'Confirm',
        onClick: () => {
          onConfirm();
        },
        primary: true
      },
      {
        text: 'Cancel',
        onClick: () => {
          onCancel();
        },
        danger: true
      }
    ],
    ...options
  });
};

export const showActionToast = (message, actionText, onAction, options = {}) => {
  return showNotificationToast({ body: message }, {
    type: 'info',
    duration: 0, // Don't auto-dismiss
    buttons: [
      {
        text: actionText,
        onClick: () => {
          onAction();
        },
        primary: true
      }
    ],
    ...options
  });
};

export const showDismissibleToast = (message, buttons = [], options = {}) => {
  return showNotificationToast({ body: message }, {
    type: 'info',
    duration: 0, // Don't auto-dismiss
    buttons: [
      ...buttons,
      {
        text: 'Dismiss',
        onClick: () => { },
        warning: true
      }
    ],
    ...options
  });
};

/**
 * Specialized notification functions with images
 */
export const showUserNotification = (title, message, userImage, options = {}) => {
  return showNotificationToast({}, {
    type: 'info',
    title,
    message,
    image: userImage,
    ...options
  });
};

export const showChatNotification = (message, senderImage, senderName, options = {}) => {
  return showNotificationToast({}, {
    type: 'info',
    title: `Message from ${senderName}`,
    message,
    image: senderImage,
    duration: 8000,
    buttons: [
      {
        text: 'Open Chat',
        onClick: () => {
          // Navigate to chat with sender
          const senderId = options.senderId;
          if (senderId) {
            window.location.href = `/chat/${senderId}`;
          } else {
            window.location.href = '/chat';
          }
        },
        primary: true
      }
    ],
    ...options
  });
};

export const showAppointmentNotification = (title, message, doctorImage, options = {}) => {
  return showNotificationToast({}, {
    type: 'info',
    title,
    message,
    image: doctorImage,
    duration: 10000,
    ...options
  });
};

/**
 * Utility functions
 */
export const dismissAllToasts = () => {
  if (toastContainer) {
    const toasts = toastContainer.querySelectorAll('.custom-toast');
    toasts.forEach(toast => removeToast(toast));
  }
};

export const getToastCount = () => {
  if (toastContainer) {
    return toastContainer.querySelectorAll('.custom-toast').length;
  }
  return 0;
};